import { resolveFieldEmptyLabel, resolveFieldValueColor } from '@components/v2/fieldValue.utils';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';

interface DetailsFieldValueProps {
  value?: ReactNode;
  title?: string;
  icon?: ReactNode;
  hasError?: boolean;
  isMuted?: boolean;
  onEdit?: () => void;
}

const isEmptyValue = (value: ReactNode) => value === undefined || value === null || value === '';

export const DetailsFieldValue = ({
  value,
  title,
  icon,
  hasError,
  isMuted,
  onEdit,
}: DetailsFieldValueProps) => {
  const { translate } = useTranslation();

  const isEditable = Boolean(onEdit);
  const isEmpty = isEmptyValue(value);
  const emptyLabel = resolveFieldEmptyLabel({ isEditable, placeholder: LangEnum.GLOBAL_ADD });

  return (
    <Box
      component={isEditable ? 'button' : 'div'}
      type={isEditable ? 'button' : undefined}
      onClick={onEdit}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flex: 1,
        minWidth: 0,
        px: 0.75,
        py: 0.5,
        border: 'none',
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        bgcolor: 'transparent',
        font: 'inherit',
        textAlign: 'left',
        cursor: isEditable ? 'pointer' : 'default',
        '&:hover': isEditable ? { bgcolor: 'action.hover' } : undefined,
      })}
    >
      {icon}
      <Typography
        variant="body2"
        noWrap
        title={title ?? (typeof value === 'string' ? value : undefined)}
        sx={{
          minWidth: 0,
          color: resolveFieldValueColor({ hasError, isEmpty, isEditable, isMuted }),
        }}
      >
        {isEmpty ? translate(emptyLabel) : value}
      </Typography>
    </Box>
  );
};
